import { ClaudeTool, Command, CommandId, SelfContainedCommand } from 'types/commands';
import { CommandHandler } from 'types/commands';
import { create } from 'zustand';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('COMMAND_REGISTRY', { enabled: false });

// Define the command registry state
interface CommandRegistryState {
  commands: Map<CommandId, CommandHandler>;
  commandDefinitions: Map<CommandId, Command | SelfContainedCommand>;
}

// Define the command registry store
interface CommandRegistryStore extends CommandRegistryState {
  registerCommand: (id: CommandId, handler: CommandHandler) => void;
  unregisterCommand: (id: CommandId) => void;
  executeCommand: <T>(id: CommandId, params: any, context?: any, onProgress?: (progress: any) => void) => Promise<T | undefined>;
  registerCommandDefinition: (command: Command | SelfContainedCommand) => void;
  getCommandDefinition: (id: CommandId) => Command | SelfContainedCommand | undefined;
}

// Create the command registry store
export const useCommandRegistryStore = create<CommandRegistryStore>((set, get) => ({
  commands: new Map(),
  commandDefinitions: new Map(),

  registerCommand: (id: CommandId, handler: CommandHandler) => {
    set((state) => {
      const newCommands = new Map(state.commands);
      newCommands.set(id, handler);
      return { commands: newCommands };
    });
  },

  unregisterCommand: (id: CommandId) => {
    set((state) => {
      const newCommands = new Map(state.commands);
      newCommands.delete(id);
      return { commands: newCommands };
    });
  },

  executeCommand: async <T>(id: CommandId, params: any, context?: any, onProgress?: (progress: any) => void): Promise<T | undefined> => {
    let { commands, commandDefinitions } = get();

    // Primary lookup in the handler cache; fall back to commandDefinitions if the cache is stale
    let handler = commands.get(id) ?? (commandDefinitions.get(id) as any)?.handler;

    // If not found, re-initialize the full registry and retry.
    // This handles partial registry states (e.g. only tool commands registered via HMR/lazy-load,
    // but initializeAllCommands never ran for this module instance).
    if (!handler) {
      const log = createFileLogger('COMMAND_REGISTRY');
      log.warn(`[CommandRegistry] Command "${id}" not found (registry has ${commandDefinitions.size} commands). Re-initializing...`);
      await initializeAllCommands();
      ({ commands, commandDefinitions } = get());
      handler = commands.get(id) ?? (commandDefinitions.get(id) as any)?.handler;
    }
    
    if (handler) {
      try {
        // Build the execution context, including the onProgress callback
        const executionContext = {
          ...(context || {}),
          onProgress
        };

        let result;
        
        // New standard: handler(params, context)
        // Self-contained commands and new legacy commands follow this
        if (handler.length >= 2) {
          result = await handler(params, executionContext);
        } else {
          // Legacy: handler({ ...params, context })
          const legacyParams = {
            ...params,
            context: executionContext
          };
          result = await handler(legacyParams);
        }

        return result;
      } catch (error) {
        log.error(`Error executing command ${id}:`, error);
        
        // Re-throw the error so it can be caught by the CommandExecutor
        throw error;
      }
    } else {
      log.warn(`Command ${id} not found`);
      
      // Throw an error instead of returning undefined
      throw new Error(`Command not found: ${id}`);
    }
  },

  registerCommandDefinition: (command: Command | SelfContainedCommand) => {
    // Attach a default formatResultPreview if missing, to ensure completed cards show a meaningful label
    const enhanced = { ...command } as any;
    if (typeof enhanced.formatResultPreview !== 'function') {
      enhanced.formatResultPreview = (result: any, toolInput: any) => {
        try {
          // Prefer StandardToolResult.message
          if (result && typeof result.message === 'string' && result.message.length > 0) {
            return result.message;
          }
          // Prefer explicit summaries on data
          if (result && result.data) {
            const data = result.data;
            if (typeof data.summary === 'string' && data.summary.length > 0) return data.summary;
            // Generic search-style summaries
            const arrays = [
              ['results', Array.isArray((data as any).results) ? (data as any).results.length : undefined],
              ['hits', Array.isArray((data as any).hits) ? (data as any).hits.length : undefined],
              ['nodes', Array.isArray((data as any).nodes) ? (data as any).nodes.length : undefined],
              ['items', Array.isArray((data as any).items) ? (data as any).items.length : undefined],
              ['matches', Array.isArray((data as any).matches) ? (data as any).matches.length : undefined],
            ].filter(([_, v]) => typeof v === 'number' && (v as number) >= 0) as Array<[string, number]>;
            if (arrays.length > 0) {
              const [label, count] = arrays[0];
              return `Found ${count} ${label}`;
            }
            if (typeof (data as any).total === 'number') return `Found ${(data as any).total} results`;
            if (typeof (data as any).count === 'number') return `Found ${(data as any).count} results`;
            // ✅ ENHANCED: Use enhanced message context if available
            if (data.messageContext) {
              const ctx = data.messageContext;
              if (typeof ctx.operationSummary === 'string') {
                const preview = ctx.contentPreview ? ` - ${ctx.contentPreview}` : '';
                return `${ctx.operationSummary}${preview}`;
              }
            }
            // Common fields across doc tools
            if (typeof data.replacedCount === 'number') return `Replaced ${data.replacedCount} node(s)`;
            if (typeof data.removedCount === 'number') return `Removed ${data.removedCount} node(s)`;
            if (Array.isArray(data.insertedNodeIds)) return `Inserted ${data.insertedNodeIds.length} node(s)`;
            if (typeof data.type === 'string') return `Completed ${data.type.replace(/^document_/, '').replace(/_/g, ' ')}`;
          }
          // Fallback to input-provided expand text
          if (toolInput && typeof toolInput.expand === 'string' && toolInput.expand.length > 0) {
            return toolInput.expand;
          }
        } catch {}
        return 'Completed';
      };
    }
    set((state) => {
      const newDefinitions = new Map(state.commandDefinitions);
      newDefinitions.set(enhanced.id, enhanced);
      return { commandDefinitions: newDefinitions };
    });
  },

  getCommandDefinition: (id: CommandId) => {
    const { commandDefinitions } = get();
    return commandDefinitions.get(id);
  },
}));

export const createToolsFromCommands = (commands: (Command | SelfContainedCommand)[]): ClaudeTool[] => {
  return commands.map(command => {
    // Handle both Command and SelfContainedCommand types
    const parameters = 'parameters' in command 
      ? command.parameters // Legacy Command
      : command.schema.parameters; // SelfContainedCommand

    return {
      name: command.id,
      description: command.description,
      input_schema: {
        type: "object",
        properties: Object.fromEntries(
          parameters.map((param: any) => {
            const propertyDef: any = {
              type: param.type,
              description: param.description
            };
            
            // Include default value if present (helps Claude understand optional parameters)
            if (param.defaultValue !== undefined) {
              propertyDef.default = param.defaultValue;
            }
            
            // For array types, include items schema if present (critical for Claude to understand array structure)
            if (param.type === 'array' && param.items) {
              propertyDef.items = param.items;
            }
            
            return [param.name, propertyDef];
          })
        ),
        required: parameters
          .filter((param: any) => param.required)
          .map((param: any) => param.name)
      }
    };
  });
};

export const registerCommands = (commands: (SelfContainedCommand | Command)[]) => {
  const commandRegistry = useCommandRegistryStore.getState();
  commands.forEach(command => {
    commandRegistry.registerCommand(command.id, command.handler);
    // Store the command directly - registry now supports both types
    commandRegistry.registerCommandDefinition(command);
  });
};

// NEW: Register self-contained commands
export const registerSelfContainedCommands = (commands: any[]) => {
  const commandRegistry = useCommandRegistryStore.getState();
  const registeredIds: string[] = [];
  commands.forEach(command => {
    // Self-contained commands have a different handler signature but the registry doesn't care
    commandRegistry.registerCommand(command.id, command.handler);
    commandRegistry.registerCommandDefinition(command);
    registeredIds.push(command.id);
  });
  
};

// Initialize all app commands — async so heavy app deps are loaded on demand
// and never appear in the critical page-load module graph.
export const initializeAllCommands = async (): Promise<void> => {
  log.info('[CommandRegistry] Starting command initialization...');
  
  // Clear existing commands via set() so Zustand state is consistent
  useCommandRegistryStore.setState({
    commands: new Map(),
    commandDefinitions: new Map(),
  });

  // Load all command modules in parallel — none of these are in the static import
  // graph so their transitive deps (prosemirror, yjs, pdf, etc.) stay out of the
  // critical page-load path and are fetched only when this function runs.
  const [
    spaceUIMod,
    { billingCommands },
    { entityCommands },
    { commands: docsCommands },
    { markdownDocCommands: v5DocsCommands },
    { commands: pdfiumCommands },
    { commands: _bankCommandsImport },
    { browserCommands },
    { userInteractionCommands },
    { UNIVERSAL_COMMANDS },
    { writersDashboardCreateCommand },
    { dashboardCreateCommand },
    { manuscriptReviewReportCreateCommand },
    { bookProjectCreateCommand },
    { bookWriteCommand },
    { listenAppCommands },
    { libraryCommands },
    { tasksCommands },
    { characterCommands },
    { notesCommands },
    { DATABASE_COMMANDS: databaseCommands },
    { VIDEO_STUDIO_PROJECT_COMMANDS, VIDEO_STUDIO_COMMANDS },
    { SHEETS_COMMANDS },
    { PRESENTATIONS_COMMANDS },
    { googleDriveCommands },
    { wordPressCommands },
    { toolCommands },
    { newWindowCommands },
    { openDialogCommands },
    { canvasCommands },
    { apiConnectCommand, publicApiQueryCommand, enableAPIConnectionCommand, disableAPIConnectionCommand, apiConnectionReadCommand },
    { apiConnectionCommands },
    { computeSessionCommand },
    { mailAppCommands },
    { AUTOMATIONS_COMMANDS },
    { planCreateCommand },
    { todoWriteCommand, todoReadCommand, planUpdateCommand },
    { pageCommands },
    { transcriptCreateCommand },
    { contactsCommands },
    { appBuilderCommands },
    { rulesCommands },
    { promptsCommands },
    { memoryCommands },
    { markdownDocCommands },
    { inboxCommands },
    { shopCommands },
    { passwordVaultCommands },
    { DOC_SYSTEM_COMMANDS },
    { CANVAS_SYSTEM_COMMANDS },
    { IMAGE_STUDIO_COMMANDS },
    { aiCommands },
    { registerMediaCommandsInRegistry },
  ] = await Promise.all([
    import('../systemCommands/spaceUI'),
    import('../systemCommands/billing/billingCommands'),
    import('../systemCommands/entitySystem'),
    import('../apps/DocsApp/store/commands'),
    import('../apps/DocsApp/commands/v5'),
    import('../apps/PDFiumApp/commands'),
    import('../apps/BankApp/store/commands'),
    import('../apps/BrowserApp/commands'),
    import('../systemCommands/userInteraction'),
    import('../apps/UniversalCommands'),
    import('../apps/WritersDashboardApp_v2/commands/writers_dashboard_create'),
    import('../apps/DashboardApp/commands/dashboard_create'),
    import('../apps/ManuscriptReviewReportApp_v2/commands/manuscript_review_report_create'),
    import('../apps/BookFormatterApp_v2/commands/book_project_create'),
    import('../apps/BookFormatterApp_v2/commands/book_write'),
    import('../apps/ListenApp/commands'),
    import('../apps/LibraryApp/commands'),
    import('../apps/TasksApp/store/commands'),
    import('../commands/character'),
    import('../apps/NotesMarkdownApp/commands'),
    import('../apps/DatabaseApp/commands'),
    import('../apps/VideoStudioApp/commands'),
    import('../apps/SheetsApp_v2/commands'),
    import('../apps/PresentationsApp_v2/commands'),
    import('../apps/GoogleDriveApp/commands'),
    import('../apps/WordPressApp/commands'),
    import('../systemCommands/execution/local'),
    import('../apps/NewWindowApp/commands'),
    import('../commands/openDialog'),
    import('../apps/CanvasApp/commands'),
    import('../systemCommands/integrations'),
    import('../apps/APIConnectionApp/commands'),
    import('../systemCommands/execution/cloud/computeSession'),
    import('../apps/MailApp/commands'),
    import('../apps/AutomationsApp/commands/v1'),
    import('../apps/PlanApp/commands/v1'),
    import('../systemCommands/agentTools/todo_write'),
    import('../apps/PageApp_v2/commands/v1'),
    import('../apps/TranscriptApp/commands/v1/transcript_create'),
    import('../apps/ContactsApp/commands/v1'),
    import('../apps/AppBuilderApp/commands'),
    import('../apps/RulesApp_v2/commands'),
    import('../apps/PromptsApp_v2/commands'),
    import('../apps/MemoryApp_v2/commands'),
    import('../apps/MarkdownApp_v2/commands'),
    import('../apps/InboxApp/commands'),
    import('../commands/shop'),
    import('../apps/PasswordVaultApp/commands'),
    import('../systemCommands/docSystem'),
    import('../systemCommands/canvasSystem'),
    import('../apps/MediaViewerApp/commands/imageStudioCommands'),
    import('./aiCommands'),
    import('../services/mediaCommandService'),
  ]);

  const {
    spaceLifecycleCommands,
    windowManagementCommands,
    panelVisibilityCommands,
    sidebarTreeCommands,
    workflowUtilityCommands,
    driveNavigationCommands,
    codebaseWorkspaceCommands,
    setupCodebaseWorkspaceEventListener,
  } = spaceUIMod;

  const bankCommands: any[] = _bankCommandsImport || [];

  registerSelfContainedCommands(spaceLifecycleCommands);
  registerSelfContainedCommands(windowManagementCommands);
  registerSelfContainedCommands(panelVisibilityCommands);
  registerSelfContainedCommands(sidebarTreeCommands);
  registerSelfContainedCommands(workflowUtilityCommands);
  registerSelfContainedCommands(driveNavigationCommands);
  registerSelfContainedCommands(codebaseWorkspaceCommands);
  setupCodebaseWorkspaceEventListener();
  registerSelfContainedCommands(billingCommands);
  
  registerSelfContainedCommands(entityCommands);

  const activeDocsCommands = docsCommands;
  log.info('[CommandRegistry] Got active docs commands:', {
    count: activeDocsCommands.length,
    ids: activeDocsCommands.map(c => c.id),
    yjsCount: activeDocsCommands.filter(c => c.id.startsWith('yjs_')).length
  });
  
  registerCommands(activeDocsCommands);

  // Register the new V5 markdown commands
  registerSelfContainedCommands(v5DocsCommands);
  
  registerCommands(pdfiumCommands);
  registerCommands(bankCommands);
  registerCommands(browserCommands);
  
  registerSelfContainedCommands(userInteractionCommands);
  registerSelfContainedCommands(aiCommands);
  registerSelfContainedCommands(tasksCommands);
  registerSelfContainedCommands(passwordVaultCommands);
  registerSelfContainedCommands(characterCommands);
  registerSelfContainedCommands(notesCommands);
  registerSelfContainedCommands(databaseCommands);

  // Register VideoStudioApp — project creation + thin video_* tool wrappers.
  registerSelfContainedCommands(VIDEO_STUDIO_PROJECT_COMMANDS);
  registerSelfContainedCommands(VIDEO_STUDIO_COMMANDS);
  
  registerSelfContainedCommands(SHEETS_COMMANDS);
  registerSelfContainedCommands(PRESENTATIONS_COMMANDS);
  registerSelfContainedCommands([dashboardCreateCommand]);
  registerSelfContainedCommands([writersDashboardCreateCommand]);
  registerSelfContainedCommands([manuscriptReviewReportCreateCommand]);
  registerSelfContainedCommands([bookProjectCreateCommand, bookWriteCommand]);
  registerSelfContainedCommands(listenAppCommands);
  registerSelfContainedCommands(libraryCommands);
  registerSelfContainedCommands(mailAppCommands);
  registerSelfContainedCommands(AUTOMATIONS_COMMANDS);
  registerSelfContainedCommands([planCreateCommand]);
  registerSelfContainedCommands([todoWriteCommand, todoReadCommand, planUpdateCommand]);
  registerSelfContainedCommands(contactsCommands);
  registerSelfContainedCommands(pageCommands);
  registerSelfContainedCommands([transcriptCreateCommand]);
  registerSelfContainedCommands(googleDriveCommands);
  registerSelfContainedCommands(wordPressCommands);
  registerSelfContainedCommands(toolCommands);
  registerSelfContainedCommands([
    apiConnectCommand,
    publicApiQueryCommand,
    apiConnectionReadCommand,
    enableAPIConnectionCommand,
    disableAPIConnectionCommand
  ]);
  registerSelfContainedCommands(apiConnectionCommands);
  log.info('[CommandRegistry] ✅ API Connection commands registered:', {
    systemCommands: ['api_connect', 'public_api_query', 'api_connection_read', 'enable_api_connection', 'disable_api_connection'],
    appCommands: apiConnectionCommands.map(c => c.id)
  });
  
  registerSelfContainedCommands([computeSessionCommand]);
  log.info('[CommandRegistry] ✅ Compute Session command registered:', {
    computeSession: 'compute_session'
  });
  
  registerSelfContainedCommands(newWindowCommands);
  registerSelfContainedCommands(openDialogCommands);
  registerSelfContainedCommands(canvasCommands);
  registerSelfContainedCommands(appBuilderCommands);
  registerSelfContainedCommands(shopCommands);
  log.info('[CommandRegistry] ✅ App Builder commands registered:', {
    count: appBuilderCommands.length,
    ids: appBuilderCommands.map(c => c.id)
  });
  
  registerSelfContainedCommands(rulesCommands);
  registerSelfContainedCommands(promptsCommands);
  registerSelfContainedCommands(memoryCommands);
  registerSelfContainedCommands(markdownDocCommands);
  registerSelfContainedCommands(inboxCommands);
  registerSelfContainedCommands(DOC_SYSTEM_COMMANDS);
  registerSelfContainedCommands(CANVAS_SYSTEM_COMMANDS);

  // Register Image Studio agent-broadcast handlers (image_studio_progress / _result).
  // These must be global so an agent-side tool can deliver results even before
  // any ImageStudio window is mounted, and must NOT be re-registered per-mount.
  registerSelfContainedCommands(IMAGE_STUDIO_COMMANDS);

  registerSelfContainedCommands(UNIVERSAL_COMMANDS);
  log.info('[CommandRegistry] ✅ Registered Universal Commands:', {
    count: UNIVERSAL_COMMANDS.length,
    ids: UNIVERSAL_COMMANDS.map(c => c.id)
  });

  // Register media commands (media_speak, window_action_poll).
  // Must be inside initializeAllCommands so they survive any re-initialization
  // of the full command map (previously they were mount-time-only and would
  // disappear if the registry was re-initialized after Space.tsx unmounted).
  registerMediaCommandsInRegistry(useCommandRegistryStore.getState());
  
  log.info('[CommandRegistry] ✅ Initialized all commands:', {
    total: useCommandRegistryStore.getState().commandDefinitions.size,
  });
};

/**
 * Registry-First Pattern: Get commands for specific contexts
 * This is the SINGLE source of truth for all command access
 */
export const getCommandsForContext = (context: 'api' | 'tools' | 'ui' | 'all' = 'all'): (Command | SelfContainedCommand)[] => {
  const registry = useCommandRegistryStore.getState();
  const allCommands = Array.from(registry.commandDefinitions.values());
  
  // Context-specific filtering
  switch (context) {
    case 'api':
      // For backend API - ensure no duplicates, include all essential commands
      return allCommands.filter(() => {
        // Include all commands but ensure docs commands are properly filtered
        return true;
      });
      
    case 'tools':
      // For tool discovery - include commands that should be exposed to AI
      return allCommands.filter(cmd => {
        // Exclude internal/debug commands if needed
        return !cmd.id.includes('debug') && !cmd.id.includes('internal');
      });
      
    case 'ui':
      // For UI contexts - include commands for contextual tool selection
      return allCommands;
      
    case 'all':
    default:
      return allCommands;
  }
};

/**
 * Get commands by app type for contextual filtering
 */
export const getCommandsByApp = (appType: string): (Command | SelfContainedCommand)[] => {
  const allCommands = getCommandsForContext('all');
  
  // Filter commands by app prefix or app-specific logic
  const filtered = allCommands.filter(cmd => {
    if (appType === 'docs' || appType === 'doc') {
      return cmd.id.startsWith('docs_') || cmd.id.startsWith('yjs_') || cmd.id.startsWith('doc_');
    }
    if (appType === 'notes' || appType === 'note') {
      return cmd.id.startsWith('notes_');
    }
    if (appType === 'pdf' || appType === 'pdfium') {
      return cmd.id.startsWith('pdf_') || cmd.id.startsWith('pdfium_');
    }
    if (appType === 'bank') {
      return cmd.id.startsWith('bank_');
    }
    if (appType === 'google-drive') {
      return cmd.id.startsWith('google_');
    }
    if (appType === 'tasks') {
      return cmd.id.startsWith('task_') || cmd.id.startsWith('tasks_');
    }
    if (appType === 'book') {
      return cmd.id.startsWith('book_') || cmd.id === 'entity_read';
    }
    // ✅ UNIVERSAL: Commands that work with ANY v2 entity
    if (appType === 'universal') {
      return cmd.id.startsWith('entity_');
    }
    return false;
  });
  
  if (appType === 'docs' || appType === 'doc') {
    log.debug('[CommandRegistry][getCommandsByApp] Docs commands filtered:', {
      appType,
      totalCommands: allCommands.length,
      docsCommands: filtered.length,
      docsCommandIds: filtered.map(c => c.id),
      yjsCommands: filtered.filter(c => c.id.startsWith('yjs_')).length,
      legacyCommands: filtered.filter(c => c.id.startsWith('docs_')).length,
      commands4: filtered.filter(c => c.id.startsWith('doc_')).length
    });
  }
  
  if (appType === 'google-drive') {
    log.log('[google tools feature] Google Drive commands filtered:', {
      appType,
      totalCommands: allCommands.length,
      googleDriveCommands: filtered.length,
      commandIds: filtered.map(c => c.id)
    });
  }
  
  return filtered;
};

/**
 * Ensure commands are initialized before accessing them
 */
export const ensureCommandsInitialized = async (): Promise<void> => {
  const registry = useCommandRegistryStore.getState();
  if (registry.commandDefinitions.size === 0) {
    log.warn('[CommandRegistry] Commands not initialized, initializing now...');
    await initializeAllCommands();
  }
};

/** Convert JSON Schema properties to the SelfContainedCommand parameter format. */
function buildParametersFromSchema(inputSchema: Record<string, any>): any[] {
  const properties = inputSchema?.properties ?? {};
  const required: string[] = inputSchema?.required ?? [];

  return Object.entries(properties).map(([name, def]: [string, any]) => ({
    name,
    type: def.type ?? 'string',
    description: def.description ?? '',
    required: required.includes(name),
    enum: def.enum,
    defaultValue: def.default,
  }));
}

/**
 * Register commands from installed App Bundles.
 * Called asynchronously after initializeAllCommands() — non-fatal if it fails.
 * Bundle commands delegate to callToolRuntime() just like first-party commands.
 * Built-in commands always win name conflicts.
 */
export const registerBundleCommands = (bundles: Array<{ bundleId: string; bundleName: string; tools: Array<{ name: string; description: string; inputSchema: Record<string, any>; handlerUrl: string }> }>): void => {
  const registry = useCommandRegistryStore.getState();
  let registered = 0;
  let skipped = 0;

  for (const bundle of bundles) {
    for (const tool of bundle.tools) {
      // Built-in wins on conflict
      if (registry.commands.has(tool.name as any)) {
        log.warn(`[CommandRegistry] ⚠️ bundle command "${tool.name}" conflicts with built-in — skipping`);
        skipped++;
        continue;
      }

      const command: any = {
        id: tool.name,
        description: tool.description,
        schema: {
          parameters: buildParametersFromSchema(tool.inputSchema),
        },
        handler: async (params: any, context: any) => {
          const { callToolRuntime } = await import('../utils/callToolRuntime');
          return callToolRuntime(tool.name, params, context);
        },
      };

      registry.registerCommand(tool.name as any, command.handler);
      registry.registerCommandDefinition(command);
      registered++;
    }
  }

  if (registered > 0 || skipped > 0) {
    log.info(`[CommandRegistry] registered ${registered} bundle commands from ${bundles.length} installed bundles`, {
      skipped,
    });
  }
};
